import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiArrowRight } from 'react-icons/fi';
import { searchEngine, SearchResult } from '../../lib/search/searchEngine';
import './ServiceSearch.css';

interface ServiceSearchProps {
  serviceKey?: string;     // Filter to current service (e.g. 'contacts', 'accounting')
  serviceName?: string;    // Display name (e.g. 'Contacts')
  placeholder?: string;
}

const ServiceSearch: React.FC<ServiceSearchProps> = ({
  serviceKey,
  serviceName,
  placeholder,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const placeholderText = placeholder || (serviceName ? `Search ${serviceName}...` : 'Search this module...');

  // Search on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(-1);
      return;
    }

    const timer = setTimeout(() => {
      const res = searchEngine.search(query, {
        limit: 10,
        serviceFilter: serviceKey,
      });
      setResults(res);
      setSelectedIndex(-1);
    }, 50);

    return () => clearTimeout(timer);
  }, [query, serviceKey]);

  const handleSelect = useCallback((result: SearchResult) => {
    navigate(result.item.path);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  }, [navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setQuery('');
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="service-search" ref={wrapperRef}>
      <div className="service-search-bar">
        <FiSearch className="service-search-icon" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholderText}
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="service-search-input"
        />
        {query && (
          <button className="service-search-clear" onClick={() => { setQuery(''); setResults([]); }}>
            <FiX size={14} />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && query.trim() && (
        <div className="service-search-dropdown">
          {results.length > 0 ? (
            <>
              {results.map((result, idx) => (
                <button
                  key={result.item.id}
                  className={`service-result-item ${idx === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <div className="service-result-text">
                    <span className="service-result-title">{result.item.title}</span>
                    {result.item.subtitle && (
                      <span className="service-result-subtitle">{result.item.subtitle}</span>
                    )}
                  </div>
                  <span className="service-result-type">{result.item.type}</span>
                  <FiArrowRight className="service-result-arrow" size={12} />
                </button>
              ))}
              <div className="service-search-footer">
                {results.length} result{results.length !== 1 ? 's' : ''}
                {serviceName && ` in ${serviceName}`}
              </div>
            </>
          ) : (
            <div className="service-no-results">
              No results for "{query}"{serviceName && ` in ${serviceName}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceSearch;
